import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../utils/apiConfig';
import { useLocation } from './LocationContext';

const InventoryContext = createContext();

// Action types
const INVENTORY_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_INVENTORY: 'SET_INVENTORY',
  SET_STATS: 'SET_STATS',
  SET_LOW_STOCK: 'SET_LOW_STOCK',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_FILTERS: 'SET_FILTERS',
  SET_PAGINATION: 'SET_PAGINATION'
};

// Initial state
const initialState = {
  items: [],
  stats: {
    totalItems: 0,
    activeItems: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalValue: 0,
    categoryBreakdown: []
  },
  lowStockItems: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false
  },
  filters: {
    search: '',
    category: '',
    status: '',
    sortBy: 'name',
    sortOrder: 'asc'
  },
  loading: false,
  error: null
};

// Reducer
const inventoryReducer = (state, action) => {
  switch (action.type) {
    case INVENTORY_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case INVENTORY_ACTIONS.SET_INVENTORY:
      return {
        ...state,
        items: action.payload.items,
        pagination: action.payload.pagination,
        loading: false,
        error: null
      };
    
    case INVENTORY_ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload,
        loading: false,
        error: null
      };
    
    case INVENTORY_ACTIONS.SET_LOW_STOCK:
      return {
        ...state,
        lowStockItems: action.payload,
        loading: false,
        error: null
      };
    
    case INVENTORY_ACTIONS.ADD_ITEM:
      return {
        ...state,
        items: [action.payload, ...state.items],
        stats: {
          ...state.stats,
          totalItems: state.stats.totalItems + 1,
          activeItems: action.payload.status === 'active' ? state.stats.activeItems + 1 : state.stats.activeItems
        }
      };
    
    case INVENTORY_ACTIONS.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map(item => 
          item._id === action.payload._id ? action.payload : item
        )
      };
    
    case INVENTORY_ACTIONS.DELETE_ITEM:
      const deletedItem = state.items.find(item => item._id === action.payload);
      return {
        ...state,
        items: state.items.filter(item => item._id !== action.payload),
        stats: {
          ...state.stats,
          totalItems: state.stats.totalItems - 1,
          activeItems: deletedItem?.status === 'active' ? state.stats.activeItems - 1 : state.stats.activeItems
        }
      };
    
    case INVENTORY_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case INVENTORY_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    case INVENTORY_ACTIONS.SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    case INVENTORY_ACTIONS.SET_PAGINATION:
      return { ...state, pagination: { ...state.pagination, ...action.payload } };
    
    default:
      return state;
  }
};

// Provider component
export const InventoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);
  const { selectedLocation } = useLocation();

  // Load inventory items
  const loadInventory = useCallback(async (page = 1, filters = {}, pageSize = null) => {
    if (!selectedLocation?._id) return;

    try {
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: true });
      
      const params = new URLSearchParams({
        locationId: selectedLocation._id,
        page: page.toString(),
        limit: (pageSize || state.pagination.itemsPerPage).toString(),
        pageSize: (pageSize || state.pagination.itemsPerPage).toString(), // Some APIs expect pageSize
        visibility: 'PRIVATE',  // Stock items are PRIVATE
        customerLinked: 'false', // Stock items are not linked to customers
        ...state.filters,
        ...filters,
        _ts: Date.now().toString() // cache buster
      });

      const response = await api.get(`/inventory?${params}`, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });
      
      if (response.data.success) {
        console.debug('InventoryContext.loadInventory: items fetched =', Array.isArray(response.data?.data?.items) ? response.data.data.items.length : 'n/a');
        console.debug('InventoryContext.loadInventory: pagination =', response.data.data.pagination);
        dispatch({
          type: INVENTORY_ACTIONS.SET_INVENTORY,
          payload: {
            items: response.data.data.items || [],
            pagination: {
              currentPage: response.data.data.pagination?.currentPage || page,
              totalPages: response.data.data.pagination?.totalPages || 1,
              totalItems: response.data.data.pagination?.totalItems || response.data.data.pagination?.total || 0,
              total: response.data.data.pagination?.total || response.data.data.pagination?.totalItems || 0,
              itemsPerPage: pageSize || state.pagination.itemsPerPage,
              hasNextPage: response.data.data.pagination?.hasNextPage || false,
              hasPrevPage: response.data.data.pagination?.hasPrevPage || false
            }
          }
        });
      }
    } catch (error) {
      console.error('Load inventory error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load inventory'
      });
    }
  }, [selectedLocation?._id, state.pagination.itemsPerPage, state.filters]);

  // Load inventory statistics
  const loadStats = useCallback(async () => {
    if (!selectedLocation?._id) return;

    try {
      const response = await api.get(`/inventory/stats/${selectedLocation._id}`);
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.SET_STATS,
          payload: response.data.data.stats
        });
      }
    } catch (error) {
      console.error('Load stats error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load statistics'
      });
    }
  }, [selectedLocation?._id]);

  // Load low stock items
  const loadLowStock = useCallback(async () => {
    if (!selectedLocation?._id) return;

    try {
      const response = await api.get(`/inventory/low-stock?locationId=${selectedLocation._id}`);
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.SET_LOW_STOCK,
          payload: response.data.data.items
        });
      }
    } catch (error) {
      console.error('Load low stock error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load low stock items'
      });
    }
  }, [selectedLocation?._id]);

  // Add new inventory item
  const addItem = useCallback(async (itemData) => {
    if (!selectedLocation?._id) return;

    try {
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: true });
      
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(itemData).forEach(key => {
        if (key !== 'images' && itemData[key] !== undefined) {
          if (typeof itemData[key] === 'object') {
            formData.append(key, JSON.stringify(itemData[key]));
          } else {
            formData.append(key, itemData[key]);
          }
        }
      });

      // Add location ID
      formData.append('locationId', selectedLocation._id);

      // Add images if any
      if (itemData.images && itemData.images.length > 0) {
        itemData.images.forEach(image => {
          formData.append('images', image);
        });
      }

      const response = await api.post('/inventory', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.ADD_ITEM,
          payload: response.data.data.item
        });
        return response.data.data.item;
      }
    } catch (error) {
      console.error('Add item error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to add inventory item'
      });
      throw error;
    }
  }, [selectedLocation?._id]);

  // Update inventory item
  const updateItem = useCallback(async (itemId, itemData) => {
    try {
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: true });
      
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(itemData).forEach(key => {
        if (key !== 'images' && itemData[key] !== undefined) {
          if (typeof itemData[key] === 'object') {
            formData.append(key, JSON.stringify(itemData[key]));
          } else {
            formData.append(key, itemData[key]);
          }
        }
      });

      // Add images if any
      if (itemData.images && itemData.images.length > 0) {
        itemData.images.forEach(image => {
          formData.append('images', image);
        });
      }

      const response = await api.put(`/api/inventory/${itemId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.UPDATE_ITEM,
          payload: response.data.data.item
        });
        return response.data.data.item;
      }
    } catch (error) {
      console.error('Update item error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to update inventory item'
      });
      throw error;
    }
  }, []);

  // Delete inventory item
  const deleteItem = useCallback(async (itemId) => {
    try {
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.delete(`/api/inventory/${itemId}`);
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.DELETE_ITEM,
          payload: itemId
        });
      }
    } catch (error) {
      console.error('Delete item error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to delete inventory item'
      });
      throw error;
    }
  }, []);

  // Update stock quantity
  const updateStock = useCallback(async (itemId, quantity, type = 'set') => {
    try {
      const response = await api.patch(`/api/inventory/${itemId}/stock`, {
        quantity,
        type
      });
      
      if (response.data.success) {
        dispatch({
          type: INVENTORY_ACTIONS.UPDATE_ITEM,
          payload: response.data.data.item
        });
        return response.data.data.item;
      }
    } catch (error) {
      console.error('Update stock error:', error);
      dispatch({
        type: INVENTORY_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to update stock'
      });
      throw error;
    }
  }, []);

  // Set filters
  const setFilters = useCallback((filters) => {
    dispatch({ type: INVENTORY_ACTIONS.SET_FILTERS, payload: filters });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: INVENTORY_ACTIONS.CLEAR_ERROR });
  }, []);

  // Load inventory when location, filters, or pagination change
  // Using a ref to track if initial load has happened to prevent double-loading
  const initialLoadRef = React.useRef(false);
  const prevLocationRef = React.useRef(null);
  
  useEffect(() => {
    if (!selectedLocation?._id) {
      initialLoadRef.current = false;
      prevLocationRef.current = null;
      return;
    }
    
    // Only load if location actually changed or this is first load
    const locationChanged = prevLocationRef.current !== selectedLocation._id;
    
    if (locationChanged || !initialLoadRef.current) {
      prevLocationRef.current = selectedLocation._id;
      initialLoadRef.current = true;
      
      // Batch all initial loads together
      loadInventory(state.pagination.currentPage, state.filters);
      loadStats();
      loadLowStock();
    }
  }, [selectedLocation?._id]);
  
  // Separate effect for filter/pagination changes (not location changes)
  const filtersRef = React.useRef(JSON.stringify(state.filters));
  const pageRef = React.useRef(state.pagination.currentPage);
  
  useEffect(() => {
    if (!selectedLocation?._id || !initialLoadRef.current) return;
    
    const filtersChanged = filtersRef.current !== JSON.stringify(state.filters);
    const pageChanged = pageRef.current !== state.pagination.currentPage;
    
    if (filtersChanged || pageChanged) {
      filtersRef.current = JSON.stringify(state.filters);
      pageRef.current = state.pagination.currentPage;
      loadInventory(state.pagination.currentPage, state.filters);
    }
  }, [state.filters, state.pagination.currentPage, selectedLocation?._id, loadInventory]);

  const value = {
    ...state,
    loadInventory,
    loadStats,
    loadLowStock,
    addItem,
    updateItem,
    deleteItem,
    updateStock,
    setFilters,
    clearError
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

// Hook to use inventory context
export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

export default InventoryContext;
